// Shared color logic for DTR Copilot usage displays — the Settings > Users
// progress bar and its 30-day history chart both call this so a given
// percentage always renders as the exact same color in both places.
//
// Interpolates green -> yellow -> orange -> red as `percent` climbs 0-100:
// flat green through 50%, fading to yellow/orange/red between 50-90%, flat
// red from 90% on.

interface ColorStop {
  pct: number;
  rgb: [number, number, number];
}

const STOPS: ColorStop[] = [
  { pct: 0,   rgb: [0, 200, 83] },    // green (#00C853)
  { pct: 50,  rgb: [0, 200, 83] },    // still green
  { pct: 70,  rgb: [253, 216, 53] },  // yellow
  { pct: 80,  rgb: [255, 152, 0] },   // orange
  { pct: 90,  rgb: [255, 82, 82] },   // red — brightened 2026-09-18 so black label
                                       // text (see UsageProgressBarComponent's
                                       // darkText input) stays legible at the top
                                       // of the scale, not just against yellow/orange.
  { pct: 100, rgb: [255, 82, 82] },   // still red
];

// A real, static CSS gradient across the full 0-100% scale, built from the exact
// same STOPS usageColor() interpolates from — single source of truth, so the
// gradient and any flat-color reading (e.g. the 30-day history chart's per-day
// bars) can never drift apart. Used by UsageProgressBarComponent's track
// background; the "unfilled" remainder is covered by a neutral mask, revealing
// this gradient only up to the current percentage.
export function usageGradientCss(): string {
  const parts = STOPS.map(s => `rgb(${s.rgb[0]}, ${s.rgb[1]}, ${s.rgb[2]}) ${s.pct}%`);
  return `linear-gradient(to right, ${parts.join(', ')})`;
}

export function usageColor(percent: number): string {
  const p = Math.max(0, Math.min(100, percent ?? 0));
  for (let i = 0; i < STOPS.length - 1; i++) {
    const a = STOPS[i];
    const b = STOPS[i + 1];
    if (p >= a.pct && p <= b.pct) {
      const t = b.pct === a.pct ? 0 : (p - a.pct) / (b.pct - a.pct);
      const r = Math.round(a.rgb[0] + (b.rgb[0] - a.rgb[0]) * t);
      const g = Math.round(a.rgb[1] + (b.rgb[1] - a.rgb[1]) * t);
      const bl = Math.round(a.rgb[2] + (b.rgb[2] - a.rgb[2]) * t);
      return `rgb(${r}, ${g}, ${bl})`;
    }
  }
  const last = STOPS[STOPS.length - 1].rgb;
  return `rgb(${last[0]}, ${last[1]}, ${last[2]})`;
}
